/*
 * as.if hub - Verhalten.
 * Gehoert zu sections/asif-hub.liquid und assets/asif-hub.css.
 *
 * Aufgaben: Insel oder Leiste (Morph), Dialog mit Ansichten, Seitenkontext,
 * Radio-Zustand aus asif-radio-player.js, Lautstaerke, Consent- und
 * Kaufknopf-Ruecksicht, Vorschauleiste im Theme-Editor.
 *
 * SPA-Vertrag (asif-spa.js): Der Hub liegt ausserhalb von <main> und wird
 * beim Seitenwechsel nicht neu gebaut. Er hoert auf
 *   document 'asif:page:start'  -> Dialog schliessen
 *   document 'asif:page'        -> Kontext und Modus neu bestimmen
 * Alles, was vom aktuellen Dokument abhaengt (Titel, URL, Template), wird
 * zur Laufzeit gelesen, nie beim Start gemerkt.
 */
(() => {
  'use strict';

  const root = document.querySelector('[data-asif-hub]');
  if (!root || root.dataset.hubReady === '1') return;
  root.dataset.hubReady = '1';

  const q = (selector, scope = root) => scope.querySelector(selector);
  const qa = (selector, scope = root) => [...scope.querySelectorAll(selector)];

  const bar = q('[data-hub-bar]');
  const launcher = q('[data-hub-open]');
  const panel = q('#asif-hub-panel');
  const closeButton = q('[data-hub-close]');
  const scrollArea = q('[data-hub-scroll]');
  const heading = q('#asif-hub-title');
  const playButton = q('[data-hub-play]');
  const playLabel = q('[data-hub-play-label]');
  const radioGroup = q('[data-hub-group="radio"]');
  const homeView = q('[data-hub-view="home"]');
  const card = q('#asif-track-card');
  const cardBody = q('[data-hub-card-body]');
  const cardKicker = q('[data-hub-card-kicker]');
  const headingDefault = heading?.textContent || '.hub';
  const volume = q('[data-hub-volume]');
  const volumeInput = q('[data-hub-volume-input]');
  const views = qa('[data-hub-view]');
  const contextGroup = q('[data-hub-group="context"]');
  const contextRows = qa('[data-hub-context]');
  const faqList = q('[data-hub-faq-list]');
  const faqRows = qa('[data-hub-faq-category]');
  const titleNodes = qa('[data-hub-title]');
  const artistNodes = qa('[data-hub-artist]');
  const programNodes = qa('[data-hub-program]');
  const statusNodes = qa('[data-hub-status]');
  const artNodes = qa('[data-hub-art]');
  const programmeLink = q('[data-hub-programme-link]');
  const progress = q('[data-hub-progress]');
  const progressFill = progress?.firstElementChild || null;
  if (!bar || !launcher || !panel || !closeButton) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const mobileCommerce = window.matchMedia('(max-width: 749px)');
  const revealOffset = Math.max(0, Number(root.dataset.revealOffset) || 0);
  const VOLUME_KEY = 'asif_radio_volume_v1';
  const MORPH_MS = 320;

  const radio = {
    hasSession: false,
    isPlaying: false,
    isLoading: false,
    title: 'as.if.radio',
    artist: 'as.if.records'
  };
  let currentView = 'home';
  let currentCategory = '';
  let morphTimer = 0;
  let hintTimer = 0;
  let hintDistance = 0;
  let lastHintAt = Date.now();
  let lastScrollY = window.scrollY;
  let scrollFrame = 0;
  let hintEligible = false;

  /* --- Seitenkontext ------------------------------------------------- */

  const templateKey = () => document.body.dataset.asifTemplate || '';

  const isRadioPage = () => templateKey() === 'page.asif-radio' || document.body.classList.contains('asif-radio-product');

  const resolveContext = () => {
    const path = window.location.pathname.replace(/\/+$/, '');
    const template = templateKey();
    const [name, suffix = ''] = template.split('.');
    let key = '';
    let recommended = '';
    if (path === '/pages/spaces' || path === '/pages/as-if-spaces' || suffix === 'as-if-spaces') { key = 'spaces'; recommended = 'spaces'; }
    else if (path === '/pages/spirits' || path === '/pages/as-if-spirits' || suffix === 'as-if-spirits') { key = 'spirits'; recommended = 'spaces'; }
    else if (path === '/pages/schnapskartell' || suffix === 'schnapskartell') { key = 'schnapskartell'; recommended = 'products'; }
    else if (path === '/pages/as-if-studio' || path === '/pages/studio' || suffix === 'as-if-studio') { key = 'studio'; recommended = 'spaces'; }
    else if (isRadioPage()) { recommended = 'radio'; }
    else if (name === 'product') { recommended = 'products'; }
    else if (['collection', 'cart', 'search', 'list-collections'].includes(name)) { recommended = 'orders'; }
    const shop = ['product', 'collection', 'cart', 'search', 'list-collections', 'customers'].includes(name) || path.startsWith('/collections') || path.startsWith('/products') || path === '/cart';
    return { key, recommended, shop };
  };

  const applyContext = () => {
    const context = resolveContext();
    let visible = 0;
    contextRows.forEach((row) => {
      const target = row.dataset.hubContext;
      const show = target === context.key || (target === 'shop' && context.shop);
      row.hidden = !show;
      if (show) visible += 1;
    });
    if (contextGroup) contextGroup.hidden = visible === 0;

    if (faqList) {
      faqRows.forEach((row) => row.classList.toggle('is-recommended', row.dataset.hubFaqCategory === context.recommended));
      const recommendedRow = faqRows.find((row) => row.dataset.hubFaqCategory === context.recommended);
      if (recommendedRow && faqList.firstElementChild !== recommendedRow) faqList.prepend(recommendedRow);
    }

    if (programmeLink) programmeLink.setAttribute('href', isRadioPage() ? '#programm-jetzt' : '/pages/radio#programm-jetzt');
    qa('[data-hub-favorites-link]').forEach((link) => link.setAttribute('href', isRadioPage() ? '#favoriten' : '/pages/radio#favoriten'));
    hintEligible = ['spaces', 'spirits', 'studio'].includes(context.key);
    resetHint();
  };

  /* --- Modus: Insel oder Leiste -------------------------------------- */

  const wantsBar = () => isRadioPage() || radio.hasSession;

  const syncVolumeVisibility = () => {
    if (!volume) return;
    const audio = document.getElementById('rspAudio');
    volume.hidden = !(root.classList.contains('is-bar') && finePointer.matches && audio);
  };

  // Pierre 23.09.2026: Der Player kommt nicht mehr aus der Insel gewachsen
  // (die ist oben auf der Seite ausgeblendet), sondern gleitet vom unteren
  // Rand herein; ist die Insel gerade sichtbar, blendet sie vorher kurz aus.
  // Beim Pausieren gleitet er wieder hinaus, danach kommt die Insel zurueck,
  // sofern man schon unter dem ersten Bildbereich ist. Radioseite: immer Leiste.
  const BAR_IN_MS = 560;
  const BAR_OUT_MS = 400;
  const ISLAND_OUT_MS = 180;
  const ISLAND_BACK_DELAY = 220;
  let islandHoldUntil = 0;
  let modeToken = 0;
  let modeTimer = 0;
  const applyMode = () => {
    const next = wantsBar();
    const current = root.classList.contains('is-bar') && !root.classList.contains('is-bar-leaving');
    if (next !== current) {
      const animate = !reducedMotion.matches && !root.classList.contains('is-instant');
      const token = ++modeToken;
      window.clearTimeout(modeTimer);
      window.clearTimeout(morphTimer);
      root.classList.remove('is-morphing');
      bar.style.transform = '';
      if (!animate) {
        root.classList.remove('is-bar-entering', 'is-bar-leaving', 'is-island-leaving');
        root.classList.toggle('is-bar', next);
        document.documentElement.classList.toggle('asif-hub-bar', next);
      } else if (next) {
        const enter = () => {
          if (token !== modeToken) return;
          root.classList.remove('is-island-leaving', 'is-bar-leaving');
          root.classList.add('is-bar', 'is-bar-entering');
          document.documentElement.classList.add('asif-hub-bar');
          void bar.offsetWidth; // Startlage unter dem Rand festhalten.
          window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
            if (token === modeToken) root.classList.remove('is-bar-entering');
          }));
          applyMode();
        };
        const islandVisible = !root.classList.contains('is-bar') && root.classList.contains('is-ready');
        if (islandVisible) {
          root.classList.add('is-island-leaving');
          modeTimer = window.setTimeout(enter, ISLAND_OUT_MS);
        } else {
          enter();
        }
      } else {
        root.classList.remove('is-bar-entering');
        root.classList.add('is-bar-leaving');
        modeTimer = window.setTimeout(() => {
          if (token !== modeToken) return;
          // Die Insel startet unsichtbar, ohne Ueberblendung aus der Leiste
          // (is-instant), und blendet erst im naechsten Bild ruhig ein.
          islandHoldUntil = performance.now() + ISLAND_BACK_DELAY;
          root.classList.add('is-instant');
          root.classList.remove('is-bar', 'is-bar-leaving', 'is-ready');
          document.documentElement.classList.remove('asif-hub-bar');
          applyMode();
          void bar.offsetWidth;
          root.classList.remove('is-instant');
          // Kurze Pause, dann steigt die Insel ruhig auf. Vorher auch nicht per Scroll.
          window.setTimeout(syncReveal, ISLAND_BACK_DELAY + 16);
        }, BAR_OUT_MS);
        return;
      }
    }
    const shown = root.classList.contains('is-bar') && !root.classList.contains('is-bar-leaving');
    if (playButton) {
      playButton.hidden = !shown;
      playButton.disabled = !shown;
      playButton.tabIndex = shown ? 0 : -1;
    }
    // Im Leistenzustand steht das Radio oben im Dialog, sonst unten.
    if (radioGroup && homeView) {
      if (shown && homeView.firstElementChild !== radioGroup) homeView.prepend(radioGroup);
      else if (!shown && homeView.lastElementChild !== radioGroup) homeView.append(radioGroup);
    }
    if (heading) {
      const text = shown ? 'as.if.radio' : headingDefault;
      if (heading.textContent !== text) heading.textContent = text;
      heading.classList.toggle('asif-wordmark', /^(as\.if\.radio|as\.if\.records)$/.test(text));
    }
    syncVolumeVisibility();
    syncReveal();
  };

  // Pierre 23.09.2026: Der Hub (Insel) kommt auf allen Seiten erst, wenn der
  // erste Bildbereich (Buehne, Slider, Hero) ganz hinter dem Kopf verschwunden
  // ist, und blendet dann ruhig ein (asif-hub.css). Die Player-Leiste bleibt
  // davon unberuehrt. Seiten ohne grossen Einstieg: nach einer halben
  // Bildschirmhoehe. Kann die Seite nicht so weit scrollen, kommt der Hub am Ende.
  // Ein Wert im Editor (reveal_offset > 0) hat Vorrang.
  let revealHero;
  const findRevealHero = () => {
    const marked = document.querySelector('main [data-hub-reveal-after], main [allow-transparent-header]');
    if (marked) return marked;
    const first = document.querySelector('main > .shopify-section:first-child, #main > .shopify-section:first-child');
    return first || null;
  };
  const revealThreshold = () => {
    if (revealOffset > 0) return revealOffset;
    if (revealHero === undefined || (revealHero && !revealHero.isConnected)) revealHero = findRevealHero();
    const viewport = window.innerHeight || 800;
    if (!revealHero) return viewport * 0.5;
    const rect = revealHero.getBoundingClientRect();
    // Ganze Seiten als erste Section sind kein Einstieg: dann halbe Hoehe.
    if (rect.height < viewport * 0.35 || rect.height > viewport * 1.6) return viewport * 0.5;
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    return Math.max(0, rect.bottom + window.scrollY - header);
  };
  const pastReveal = () => {
    const room = document.documentElement.scrollHeight - window.innerHeight;
    return window.scrollY >= Math.min(revealThreshold(), room - 8);
  };
  const syncReveal = () => {
    const held = performance.now() < islandHoldUntil && !root.classList.contains('is-bar');
    root.classList.toggle('is-ready', wantsBar() || panel.open || root.classList.contains('is-bar-leaving') || root.classList.contains('is-bar') || (!held && pastReveal()));
  };

  /* --- Radio-Zustand ------------------------------------------------- */

  const setText = (nodes, value) => nodes.forEach((node) => { if (node.textContent !== value) node.textContent = value; node.classList.toggle('asif-wordmark', /^(as\.if\.radio|as\.if\.records)$/.test(value)); });

  // Titelfortschritt (Pierre 23.09.2026): eine CSS-Animation mit der Laufzeit des
  // Titels und negativem Versatz fuer die schon gelaufene Zeit. Kein Takt in JS.
  let progressKey = '';
  const syncProgress = (state = {}, force = false) => {
    if (!progress || !progressFill) return;
    const start = Number(state.trackStartedAt) || 0;
    const end = Number(state.trackEndsAt) || 0;
    const now = Date.now();
    const on = isRadioPage() && start > 0 && end > start && now < end + 60000;
    progress.classList.toggle('is-on', on);
    if (!on) { progressKey = ''; return; }
    const key = `${start}:${end}`;
    if (key === progressKey && !force) return;
    const fresh = key !== progressKey;
    progressKey = key;
    progressFill.style.animation = 'none';
    void progressFill.offsetWidth;
    progressFill.style.animation = `hub-progress ${end - start}ms linear ${-(now - start)}ms both`;
    if (fresh) {
      progress.classList.remove('is-fresh');
      void progress.offsetWidth;
      progress.classList.add('is-fresh');
    }
  };

  const syncRadio = (state = {}) => {
    syncProgress(state);
    const status = state.status || 'idle';
    radio.isPlaying = state.playing === true || status === 'playing';
    radio.isLoading = state.loading === true || ['connecting', 'buffering', 'retrying', 'offline'].includes(status) || (status === 'paused' && state.wantsToPlay === true);
    // Sitzung nur, solange das Radio laufen will. Pause baut die Leiste zur Insel zurueck.
    radio.hasSession = state.wantsToPlay === true || state.wanted === true || radio.isPlaying || radio.isLoading
      || state.needsResume === true;
    radio.title = state.title || 'as.if.radio';
    radio.artist = state.artist || 'as.if.records';
    const program = state.program || 'as.if.radio';
    const statusText = radio.isLoading ? 'Radio verbindet'
      : state.error ? 'Radioverbindung prüfen'
        : state.needsResume ? 'Radio wartet auf Weiterhören'
          : radio.isPlaying ? `Radio spielt: ${radio.artist} - ${radio.title}`
            : 'Radio pausiert';

    // "Radio an" neben Play, solange nichts laeuft: der eine Aufruf in der Leiste.
    const playText = radio.isPlaying || radio.isLoading ? ''
      : state.error ? 'Erneut versuchen'
        : state.needsResume ? 'Weiterhören'
          : status === 'paused' ? 'Weiterhören'
            : 'Radio an';
    if (playLabel) {
      playLabel.hidden = !playText;
      if (playText && playLabel.textContent !== playText) playLabel.textContent = playText;
    }
    playButton?.classList.toggle('has-label', Boolean(playText));
    const favoritesCount = (Array.isArray(state.favorites) ? state.favorites.length : 0) + (window.AsIfRadioCollection?.getEntries().length || 0);
    qa('.hub__control-count').forEach((node) => { node.hidden = favoritesCount === 0; });
    if (card?.open) renderCard(state);
    root.classList.toggle('has-radio-session', radio.hasSession);
    root.classList.toggle('is-radio-playing', radio.isPlaying);
    root.classList.toggle('is-radio-loading', radio.isLoading);
    setText(titleNodes, radio.title);
    setText(artistNodes, radio.artist);
    setText(programNodes, program);
    setText(statusNodes, statusText);
    artNodes.forEach((image) => {
      if (!state.artworkUrl || image.src === state.artworkUrl) return;
      image.src = state.artworkUrl;
      image.alt = '';
    });
    launcher.setAttribute('aria-label', radio.hasSession || isRadioPage()
      ? `Player öffnen: ${radio.artist} - ${radio.title}`
      : (root.getAttribute('aria-label') || 'Kontakt und Hilfe'));
    if (radio.hasSession) resetHint();
    applyMode();
  };

  /* --- Lautstaerke --------------------------------------------------- */

  let volumeBound = false;
  const setupVolume = () => {
    if (!volume || !volumeInput || volumeBound) return;
    const audio = document.getElementById('rspAudio');
    if (!audio) return;
    // Wird aus zwei Pfaden gerufen (beim Start und wenn der Player meldet, dass
    // er steht). Ohne diese Sperre haengen die Regler-Listener doppelt.
    volumeBound = true;
    const read = () => {
      try {
        const raw = window.localStorage.getItem(VOLUME_KEY);
        // Ein neuer Browser liefert null. Number(null) waere 0: stummer Erststart.
        if (raw === null || raw.trim() === '') return null;
        const stored = Number(raw);
        return Number.isFinite(stored) && stored >= 0 && stored <= 1 ? stored : null;
      } catch { return null; }
    };
    const write = (value) => { try { window.localStorage.setItem(VOLUME_KEY, String(value)); } catch {} };
    const reflect = (value) => {
      const percent = Math.round(value * 100);
      if (Number(volumeInput.value) !== percent) volumeInput.value = String(percent);
      volumeInput.setAttribute('aria-valuetext', `${percent} Prozent`);
    };
    // Auf Touchgeraeten ist der Regler verborgen; dort gilt die Geraetelautstaerke.
    // Keine gespeicherte Desktop-Stummschaltung ohne erreichbaren Regler anwenden.
    const stored = finePointer.matches ? read() : null;
    if (stored !== null) {
      try { audio.volume = stored; } catch {}
    }
    reflect(audio.volume);
    volumeInput.addEventListener('input', () => {
      const next = Math.min(1, Math.max(0, Number(volumeInput.value) / 100));
      audio.volume = next;
      audio.muted = false;
      write(next);
      reflect(next);
    });
    audio.addEventListener('volumechange', () => reflect(audio.muted ? 0 : audio.volume));
    syncVolumeVisibility();
  };

  /* --- Dialog ---------------------------------------------------------- */

  const focusables = () => qa('a[href], button:not([disabled]), summary, input', panel)
    .filter((element) => !element.hidden && element.getClientRects().length && !element.closest('[hidden]'));

  const setView = (name, category = '', focus = true) => {
    currentView = name;
    currentCategory = category;
    views.forEach((view) => {
      const match = view.dataset.hubView === name && (name !== 'faq' || view.dataset.hubCategory === category);
      view.hidden = !match;
    });
    if (scrollArea) scrollArea.scrollTop = 0;
    if (!focus) return;
    const active = views.find((view) => !view.hidden);
    const target = active?.querySelector('[data-hub-view-title]') || heading;
    window.requestAnimationFrame(() => target?.focus({ preventScroll: true }));
  };

  const refreshChannelLinks = () => {
    const context = `${document.title}\n${window.location.href}`;
    qa('[data-hub-channel="whatsapp"]').forEach((link) => {
      const message = [link.dataset.hubMessage, context].filter(Boolean).join('\n\n');
      link.href = `https://wa.me/${link.dataset.hubNumber}?text=${encodeURIComponent(message)}`;
    });
    qa('[data-hub-channel="email"]').forEach((link) => {
      const subject = link.dataset.hubSubject || root.dataset.emailSubject || 'Anfrage über as.if.records';
      link.href = `mailto:${link.dataset.hubAddress}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(context)}`;
    });
  };

  const openPanel = () => {
    if (panel.open || root.classList.contains('is-consent-blocked')) return;
    resetHint();
    applyContext();
    refreshChannelLinks();
    setView('home', '', false);
    launcher.setAttribute('aria-expanded', 'true');
    document.body.classList.add('asif-message-hub-open');
    panel.showModal();
    syncReveal();
    syncCommerce();
    window.requestAnimationFrame(() => {
      if (scrollArea) scrollArea.scrollTop = 0;
      heading?.focus({ preventScroll: true });
    });
    document.dispatchEvent(new CustomEvent('asif:hub', { detail: { state: 'open' } }));
  };

  const closePanel = () => { if (panel.open) panel.close(); };

  const backOrClose = () => {
    const active = views.find((view) => !view.hidden);
    const openFaq = active?.querySelector('.hub__faq-item[open]');
    if (openFaq) {
      openFaq.open = false;
      openFaq.querySelector('summary')?.focus({ preventScroll: true });
    } else if (currentView === 'faq') {
      setView('home');
    } else {
      closePanel();
    }
  };

  const onPanelClosed = () => {
    setView('home', '', false);
    launcher.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('asif-message-hub-open');
    syncReveal();
    syncCommerce();
    if (!root.classList.contains('is-consent-blocked') && !root.classList.contains('is-commerce-obscured')) {
      launcher.focus({ preventScroll: true });
    }
    document.dispatchEvent(new CustomEvent('asif:hub', { detail: { state: 'closed' } }));
  };

  launcher.addEventListener('click', openPanel);
  closeButton.addEventListener('click', closePanel);
  panel.addEventListener('close', onPanelClosed);
  panel.addEventListener('click', (event) => { if (event.target === panel) closePanel(); });
  panel.addEventListener('cancel', (event) => { event.preventDefault(); backOrClose(); });
  panel.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      backOrClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = focusables();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === heading || !panel.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, true);

  faqRows.forEach((row) => row.addEventListener('click', () => setView('faq', row.dataset.hubFaqCategory)));
  qa('[data-hub-back]').forEach((button) => button.addEventListener('click', () => setView('home')));
  qa('.hub__faq-item').forEach((item) => {
    item.addEventListener('toggle', () => {
      if (!item.open) return;
      item.parentElement?.querySelectorAll('.hub__faq-item[open]').forEach((other) => { if (other !== item) other.open = false; });
    });
  });
  qa('[data-hub-scroll-target]').forEach((button) => {
    button.addEventListener('click', () => {
      const target = button.dataset.hubScrollTarget ? document.querySelector(button.dataset.hubScrollTarget) : null;
      const fallback = button.dataset.hubFallbackUrl;
      closePanel();
      if (target) {
        window.setTimeout(() => target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' }), 80);
      } else if (fallback) {
        window.location.assign(fallback);
      }
    });
  });
  // Links im Dialog schliessen ihn, damit die Zielseite frei ist (auch im SPA-Modus).
  panel.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link || link.target === '_blank') return;
    if (link.getAttribute('href').startsWith('#') && isRadioPage()) closePanel();
  });

  /* --- Hinweis-Symbol (Kalender) auf Anfrageseiten ------------------- */

  const resetHint = () => {
    window.clearTimeout(hintTimer);
    hintTimer = 0;
    root.classList.remove('is-hinting');
  };
  const maybeHint = () => {
    if (!hintEligible || wantsBar() || reducedMotion.matches || panel.open || root.classList.contains('is-consent-blocked')) return;
    const y = window.scrollY;
    hintDistance += Math.abs(y - lastScrollY);
    lastScrollY = y;
    if (hintDistance < 360 || Date.now() - lastHintAt < 14000) return;
    hintDistance = 0;
    lastHintAt = Date.now();
    root.classList.add('is-hinting');
    hintTimer = window.setTimeout(resetHint, 1800);
  };

  /* --- Consent, Kaufknoepfe, Vorschauleiste --------------------------- */

  const isVisible = (element) => Boolean(element && !element.hidden && element.getClientRects().length);

  const syncConsent = () => {
    const banner = document.getElementById('shopify-pc__banner');
    const prefs = document.getElementById('shopify-pc__prefs');
    const blocked = isVisible(banner) || isVisible(prefs);
    root.classList.toggle('is-consent-blocked', blocked);
    if (blocked) resetHint();
    if (blocked && panel.open) closePanel();
  };
  const consentObserver = new MutationObserver(syncConsent);
  const observeConsent = () => {
    ['shopify-pc__banner', 'shopify-pc__prefs'].forEach((id) => {
      const element = document.getElementById(id);
      if (!element || element.dataset.hubObserved === '1') return;
      element.dataset.hubObserved = '1';
      consentObserver.observe(element, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
    });
  };
  document.addEventListener('click', (event) => {
    if (!event.target.closest?.('#shopify-pc__banner, #shopify-pc__prefs')) return;
    window.setTimeout(() => { observeConsent(); syncConsent(); }, 180);
  }, true);
  const bodyObserver = new MutationObserver(() => { observeConsent(); syncConsent(); });
  bodyObserver.observe(document.body, { childList: true });
  window.setTimeout(() => bodyObserver.disconnect(), 6000);

  const commerceInView = new Set();
  let commerceObserver = null;
  const syncCommerce = () => {
    const obscure = mobileCommerce.matches && commerceInView.size > 0 && !panel.open && !wantsBar();
    root.classList.toggle('is-commerce-obscured', obscure);
    launcher.disabled = obscure;
  };
  const watchCommerce = () => {
    commerceObserver?.disconnect();
    commerceInView.clear();
    const targets = [...document.querySelectorAll('main buy-buttons, main .shopify-payment-button')].filter((node) => !node.closest('product-sticky-bar'));
    if (!targets.length) { syncCommerce(); return; }
    commerceObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.05) commerceInView.add(entry.target);
        else commerceInView.delete(entry.target);
      });
      syncCommerce();
    }, { threshold: [0, 0.05, 1] });
    targets.forEach((target) => commerceObserver.observe(target));
  };
  mobileCommerce.addEventListener?.('change', syncCommerce);

  let previewBar = null;
  let previewObserver = null;
  const syncPreview = () => {
    const next = document.getElementById('PBarNextFrameWrapper');
    if (next !== previewBar) {
      previewObserver?.disconnect();
      previewBar = next;
      if (previewBar && 'ResizeObserver' in window) {
        previewObserver = new ResizeObserver(syncPreview);
        previewObserver.observe(previewBar);
      }
    }
    let offset = 0;
    if (previewBar) {
      const rect = previewBar.getBoundingClientRect();
      if (rect.height > 0 && rect.bottom >= window.innerHeight - 1 && rect.top < window.innerHeight) offset = Math.ceil(window.innerHeight - rect.top);
    }
    document.documentElement.style.setProperty('--hub-preview-offset', `${offset}px`);
  };
  const previewWatcher = new MutationObserver(syncPreview);
  previewWatcher.observe(document.body, { childList: true });
  window.addEventListener('resize', syncPreview, { passive: true });

  /* --- Der Platz unten in der installierten App -------------------------- */
  const standalone = () => window.matchMedia?.('(display-mode: standalone)').matches === true
    || window.matchMedia?.('(display-mode: fullscreen)').matches === true
    || window.navigator.standalone === true;

  // Keep only insets actually reported by this viewport. Screen-size guesses
  // confuse iPad, Android and home-button devices; portrait and landscape differ.
  const safeBottomByViewport = new Map();
  const safeProbe = document.createElement('div');
  safeProbe.setAttribute('aria-hidden', 'true');
  safeProbe.style.cssText = 'position:fixed;left:0;bottom:0;width:1px;pointer-events:none;visibility:hidden;height:env(safe-area-inset-bottom,0px);';
  document.body.append(safeProbe);
  const syncStandaloneInset = () => {
    const html = document.documentElement;
    const installed = standalone();
    html.classList.toggle('asif-standalone-inset', installed);
    if (!installed) {
      html.style.setProperty('--hub-measured-safe-bottom', '0px');
      return;
    }
    const orientation = window.screen?.orientation?.type
      || (window.matchMedia?.('(orientation: landscape)').matches ? 'landscape' : 'portrait');
    // Include layout width: iPad split-screen has its own geometry. Do not key on
    // visualViewport height, which changes as the keyboard/browser UI moves.
    const key = `${orientation}:${Math.round(document.documentElement.clientWidth)}`;
    const measured = Math.max(0, safeProbe.getBoundingClientRect().height);
    const safe = Math.max(measured, safeBottomByViewport.get(key) || 0);
    safeBottomByViewport.set(key, safe);
    // Preserve the existing iPhone protection during WebKit's initial zero report.
    // This narrow compatibility fallback is not used for iPad/Android and retires
    // as soon as this viewport has supplied a positive native measurement.
    const shortSide = Math.min(window.screen?.width || 0, window.screen?.height || 0);
    const longSide = Math.max(window.screen?.width || 0, window.screen?.height || 0);
    const iphoneBootstrap = /iPhone/.test(window.navigator.userAgent || '') && shortSide >= 375 && longSide >= 812;
    const fallback = iphoneBootstrap && !safe ? (orientation.startsWith('landscape') ? 21 : 34) : 0;
    html.style.setProperty('--hub-measured-safe-bottom', `${safe || fallback}px`);
  };
  let insetFrame = 0;
  const scheduleStandaloneInset = () => {
    if (insetFrame) return;
    insetFrame = window.requestAnimationFrame(() => {
      insetFrame = 0;
      syncStandaloneInset();
    });
  };
  syncStandaloneInset();
  if ('ResizeObserver' in window) new ResizeObserver(scheduleStandaloneInset).observe(safeProbe);
  window.addEventListener('resize', scheduleStandaloneInset, { passive: true });
  window.addEventListener('orientationchange', scheduleStandaloneInset, { passive: true });
  window.addEventListener('pageshow', scheduleStandaloneInset);
  window.visualViewport?.addEventListener('resize', scheduleStandaloneInset, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleStandaloneInset();
  });
  ['standalone', 'fullscreen'].forEach((mode) => {
    window.matchMedia?.(`(display-mode: ${mode})`).addEventListener?.('change', scheduleStandaloneInset);
  });

  /* --- Titelkarte: ausschließlich Details und Aktionen für einen Titel ---------- */
  let cardTrackId = '';
  let cardSource = 'current';
  let cardOpener = null;
  let cardTrackSnapshot = null;

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) { node.textContent = text; node.classList.toggle('asif-wordmark', /^(as\.if\.radio|as\.if\.records)$/.test(text)); }
    return node;
  };
  const glyph = (name) => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.classList.add('hub__card-glyph');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', { heart: 'M12 20.5 4.2 12.6A4.6 4.6 0 0 1 10.7 6l1.3 1.3L13.3 6a4.6 4.6 0 0 1 6.5 6.6Z', share: 'M12 15V3m0 0L8 7m4-4 4 4M5 11v8h14v-8', external: 'M5 12h14m-6-6 6 6-6 6', chevron: 'm9 6 6 6-6 6' }[name]);
    svg.append(path);
    return svg;
  };
  const formatStamp = (value) => {
    try { return new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(value)); } catch { return ''; }
  };
  const platformLinks = (track) => {
    const query = [track?.artist, track?.title].filter(Boolean).join(' ');
    if (!query) return [];
    return [
      { label: 'Spotify', href: `https://open.spotify.com/search/${encodeURIComponent(query)}` },
      { label: 'YouTube', href: `https://www.youtube.com/results?search_query=${encodeURIComponent(query)}` },
      { label: 'Apple Music', href: `https://music.apple.com/de/search?term=${encodeURIComponent(query)}` }
    ];
  };
  const resolveTrack = (state) => {
    if (cardSource === 'history') {
      const item = (state.history || []).find((entry) => entry.track?.id === cardTrackId);
      if (item) return { track: { ...item.track, artworkUrl: item.artworkUrl }, show: item.playlistTitle, stamp: item.playedAt, stampLabel: 'Gespielt', kicker: 'Aus der Tracklist' };
      if (cardTrackSnapshot) return cardTrackSnapshot;
    }
    if (cardSource === 'favorites') {
      const item = (state.favorites || []).find((entry) => entry.id === cardTrackId);
      if (item) return { track: item, show: '', stamp: item.savedAt, stampLabel: 'Gespeichert', kicker: 'Favorit' };
      // Keep the chosen track visible even when its heart is removed in this dialog.
      if (cardTrackSnapshot) return { ...cardTrackSnapshot, kicker: 'Titel', stamp: '', stampLabel: '' };
    }
    if (!state.trackId) return null;
    return { track: { id: state.trackId, title: state.title, artist: state.artist, album: state.album, artworkUrl: state.artworkUrl }, show: state.program, stamp: '', stampLabel: '', kicker: 'Gerade läuft' };
  };
  const heartButton = (trackId, isFavorite, className) => {
    const button = el('button', className);
    button.type = 'button';
    button.dataset.asifRadioFavorite = '';
    button.dataset.asifRadioTrackId = trackId;
    button.setAttribute('aria-pressed', isFavorite ? 'true' : 'false');
    button.append(glyph('heart'));
    const label = el('span', '', isFavorite ? 'Gespeichert' : 'Speichern');
    label.dataset.asifRadioFavoriteLabel = '';
    button.append(label);
    return button;
  };
  const artworkNode = (url, size) => {
    const box = el('span', 'hub__card-art');
    const image = document.createElement('img');
    image.alt = '';
    image.width = size;
    image.height = size;
    image.loading = 'lazy';
    image.decoding = 'async';
    image.src = url || (artNodes[0]?.getAttribute('src') || '');
    box.append(image);
    return box;
  };

  const renderCard = (state) => {
    if (!card || !cardBody) return;
    const favorites = state.favorites || [];
    const found = resolveTrack(state);
    if (found) cardTrackSnapshot = found;
    if (cardKicker) setText([cardKicker], found?.kicker || 'as.if.radio');
    const parts = [];

    if (found) {
      const { track } = found;
      const head = el('div', 'hub__card-track');
      head.append(artworkNode(track.artworkUrl, 640));
      const copy = el('div', 'hub__card-copy');
      const title = el('h3', 'hub__card-title', track.title || 'Unbekannter Titel');
      title.id = 'asif-track-card-title';
      title.tabIndex = -1;
      copy.append(title, el('p', 'hub__card-artist', track.artist || 'as.if.radio'));
      if (track.album && track.album !== track.title) copy.append(el('p', 'hub__card-meta', track.album));
      const facts = [found.show ? `Sendung: ${found.show}` : '', found.stamp ? `${found.stampLabel}: ${formatStamp(found.stamp)}` : ''].filter(Boolean).join(' · ');
      if (facts) copy.append(el('p', 'hub__card-meta', facts));
      head.append(copy);
      parts.push(head);

      const actions = el('div', 'hub__card-actions');
      if (track.id) actions.append(heartButton(track.id, favorites.some((item) => item.id === track.id), 'hub__card-button hub__card-button--heart'));
      const share = el('button', 'hub__card-button');
      share.type = 'button';
      share.dataset.asifRadioShare = '';
      share.append(glyph('share'));
      const shareLabel = el('span', '', 'Teilen');
      shareLabel.dataset.asifRadioShareLabel = '';
      share.append(shareLabel);
      actions.append(share);
      parts.push(actions);

      const links = el('nav', 'hub__card-links');
      links.setAttribute('aria-label', 'Auf Musikplattformen suchen');
      platformLinks(track).forEach((entry) => {
        const link = el('a', 'hub__card-link');
        link.href = entry.href;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.append(el('span', '', entry.label));
        link.setAttribute('aria-label', `${entry.label} (öffnet in einem neuen Tab)`);
        links.append(link);
      });
      parts.push(links);
    } else {
      const title = el('h3', 'hub__card-title', 'Gerade kein Titel gemeldet.');
      title.id = 'asif-track-card-title';
      title.tabIndex = -1;
      parts.push(title);
    }

    const focused = cardBody.contains(document.activeElement) ? document.activeElement : null;
    const focusId = focused?.dataset?.asifRadioTrackId || '';
    cardBody.replaceChildren(...parts);
    if (focusId) cardBody.querySelector(`[data-asif-radio-track-id="${CSS.escape(focusId)}"]`)?.focus({ preventScroll: true });

  };

  const openCard = ({ trackId = '', source = 'current', opener = null } = {}) => {
    if (!card || typeof card.showModal !== 'function') return;
    cardTrackId = trackId;
    cardTrackSnapshot = null;
    cardSource = source;
    cardOpener = opener;
    closePanel();
    renderCard(window.AsIfRadio?.getState?.() || {});
    if (!card.open) card.showModal();
    window.requestAnimationFrame(() => cardBody?.querySelector('#asif-track-card-title')?.focus({ preventScroll: true }));
  };
  const closeCard = () => { if (card?.open) card.close(); };
  card?.addEventListener('close', () => {
    const opener = cardOpener;
    cardOpener = null;
    if (opener?.isConnected) opener.focus({ preventScroll: true });
    else if (cardSource === 'favorites') document.getElementById('radio-favorites-title')?.focus({ preventScroll: true });
  });
  card?.addEventListener('click', (event) => {
    if (event.target === card) { closeCard(); return; }
    if (event.target.closest('[data-hub-card-close]')) { closeCard(); return; }
    if (event.target.closest('[data-asif-radio-favorite]') && cardTrackSnapshot?.track && window.AsIfRadio?.toggleFavorite) {
      event.preventDefault();
      event.stopPropagation();
      window.AsIfRadio.toggleFavorite(cardTrackSnapshot.track);
    }
  });
  card?.addEventListener('cancel', (event) => { event.preventDefault(); closeCard(); });
  qa('[data-hub-card-open]').forEach((button) => button.addEventListener('click', () => {
    openCard({ source: 'current', opener: button });
  }));
  // Radioseite und andere Skripte: document.dispatchEvent(new CustomEvent('asif:track:open', { detail: { trackId, source, opener } }))
  document.addEventListener('asif:track:open', (event) => openCard(event.detail || {}));
  document.addEventListener('asif:page:start', closeCard);

  /* --- Buehne im Bild? Dann zeigt die Leiste Marke und Sendung statt Titel --- */
  let stageObserver = null;
  const watchStage = () => {
    stageObserver?.disconnect();
    stageObserver = null;
    const stage = document.querySelector('[data-asif-radio-widget]');
    if (!stage || !('IntersectionObserver' in window)) { root.classList.remove('is-stage-visible'); return; }
    stageObserver = new IntersectionObserver((entries) => {
      // Beim weichen Seitentausch meldet die alte, schon entfernte Buehne
      // "unsichtbar". Das wuerde die Leiste kurz auf Titel umschalten.
      if (!stage.isConnected) return;
      root.classList.toggle('is-stage-visible', entries.some((entry) => entry.isIntersecting));
    }, { threshold: 0.2 });
    stageObserver.observe(stage);
  };

  /* --- Ereignisse ------------------------------------------------------ */

  window.addEventListener('scroll', () => {
    if (scrollFrame) return;
    scrollFrame = window.requestAnimationFrame(() => {
      scrollFrame = 0;
      syncReveal();
      maybeHint();
    });
  }, { passive: true });

  window.addEventListener('resize', () => { revealHero = undefined; syncReveal(); }, { passive: true });
  window.addEventListener('asif:collection-change', () => { const state = window.AsIfRadio?.getState(); if (state) syncRadio(state); });
  window.addEventListener('asif:radio-state', (event) => syncRadio(event.detail));
  window.addEventListener('asif:radio-ready', () => { if (window.AsIfRadio?.getState) syncRadio(window.AsIfRadio.getState()); setupVolume(); });
  finePointer.addEventListener?.('change', syncVolumeVisibility);

  document.addEventListener('asif:page:start', () => { closePanel(); });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) syncProgress(window.AsIfRadio?.getState?.() || {}, true);
  });
  document.addEventListener('asif:page', () => {
    syncProgress(window.AsIfRadio?.getState?.() || {}, true);
    revealHero = undefined;
    applyContext();
    applyMode();
    watchCommerce();
    watchStage();
    lastScrollY = window.scrollY;
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!(event.target instanceof Element) || !event.target.contains(root)) return;
    commerceObserver?.disconnect();
    stageObserver?.disconnect();
    consentObserver.disconnect();
    bodyObserver.disconnect();
    previewWatcher.disconnect();
    previewObserver?.disconnect();
    document.body.classList.remove('asif-message-hub-open');
    document.documentElement.classList.remove('asif-hub-bar');
  });

  /* --- Start ----------------------------------------------------------- */

  // Erster Zustand ohne Morph: sonst faehrt die Leiste auf der Radioseite
  // beim Laden aus der Ecke heraus.
  root.hidden = false;
  root.classList.add('is-instant');
  applyContext();
  observeConsent();
  syncConsent();
  syncPreview();
  syncRadio(window.AsIfRadio?.getState?.() || {});
  if (window.AsIfRadio) setupVolume();
  void root.offsetWidth;
  root.classList.remove('is-instant', 'is-morphing');
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { watchCommerce(); watchStage(); }, { once: true });
  else { watchCommerce(); watchStage(); }
  syncReveal();
})();
